import { useCallback, useContext } from 'react';

import { TokenContext } from '@/Contexts/TokenContext';
import { ContactConfig, ContactProps, ContactType } from '@/models/Contact';
import { ProgramNames } from '@/models/Program';
import { baseUrl, USCountryId } from '@/utils/constants';

import { useCustomFetch } from './useCustomFetch';

export default function useContact() {
  const { authResult } = useContext(TokenContext);
  const { customFetch } = useCustomFetch();

  const getContact = useCallback(
    async (
      contactId: number,
      previewMode = false,
      previewContacts: ContactProps[] | null = null
    ): Promise<ContactProps> => {
      if (previewMode) {
        const previewContact = previewContacts?.find((contact) => contact.id === contactId);
        if (previewContact != null) return previewContact;
        return { id: contactId, contactRelationships: [] };
      }

      const fetchUrl = `${baseUrl}contacts/${contactId}`;

      const response = await customFetch<ContactProps>(fetchUrl, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      return response;
    },
    [authResult, customFetch]
  );

  const saveContact = useCallback(
    async (info: ContactProps, previewMode = false): Promise<ContactProps> => {
      if (previewMode) {
        return {
          ...info,
          id: info.id ?? Math.abs(Date.now() + Math.floor(Math.random() * 1000)),
        };
      }

      const fetchUrl = `${baseUrl}contacts`;

      const response = await customFetch<ContactProps>(fetchUrl, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
        body: JSON.stringify(info),
      });

      return response;
    },
    [authResult, customFetch]
  );

  // this method will remove the current relationship and add the new one
  // this is used on the contact selects on the application form
  const updateContactRelationship = useCallback(
    async (
      contactId: number,
      applicationId: string | number,
      contactTypeId: number,
      previewMode = false
    ): Promise<boolean> => {
      if (previewMode) {
        return true;
      }

      if (contactId == null) {
        return false;
      }

      const fetchUrl = `${baseUrl}contacts/${contactId}/relationships?applicationId=${applicationId}&contactTypeId=${contactTypeId}`;

      await customFetch(fetchUrl, {
        method: 'PUT',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      return true;
    },
    [authResult, customFetch]
  );

  const postContactRelationship = useCallback(
    async (
      contactId: number,
      applicationId: string | number,
      contactTypeId: number,
      previewMode = false
    ): Promise<boolean> => {
      if (previewMode) {
        return true;
      }

      if (contactId == null) {
        return false;
      }

      const fetchUrl = `${baseUrl}contacts/${contactId}/relationships?applicationId=${applicationId}&contactTypeId=${contactTypeId}`;

      await customFetch(fetchUrl, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      return true;
    },
    [authResult, customFetch]
  );

  const deleteContactRelationship = useCallback(
    async (
      contactId: number,
      applicationId: string | number,
      contactTypeId: number,
      previewMode = false
    ): Promise<boolean> => {
      if (previewMode) {
        return true;
      }

      if (contactId == null) {
        return false;
      }

      const fetchUrl = `${baseUrl}contacts/${contactId}/relationships?applicationId=${applicationId}&contactTypeId=${contactTypeId}`;

      await customFetch(fetchUrl, {
        method: 'DELETE',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      return true;
    },
    [authResult, customFetch]
  );

  const isZipInValid = useCallback((zip: string, countryId: number, programId: ProgramNames) => {
    let notValid = false;
    const regex = /^\d{5}(-\d{4})?$/;
    if (programId != ProgramNames.TreeCitiesWorld || countryId == USCountryId) {
      notValid = zip == null || zip == '' || !regex.test(zip);
    } else {
      notValid = zip == null || zip == '' || zip?.toString().length > 10;
    }
    return notValid;
  }, []);

  const isContactValid = useCallback(
    (contact: ContactProps, config: ContactConfig | null, programId: ProgramNames) => {
      let contactHasMissingFields = contact?.id == null;

      if (contact?.id != null) {
        const status = [];

        status.push(contact?.firstName == null || contact?.firstName === '');
        status.push(contact?.lastName == null || contact?.lastName === '');

        if (config != null) {
          if (config.displayAddress) {
            status.push(contact.streetAddress1 == null || contact.streetAddress1 === '');
            status.push(contact.city == null || contact.city === '');

            if (contact.zipCode == null || contact.countryId == null || programId == null) {
              status.push(true);
            } else {
              status.push(isZipInValid(contact.zipCode, contact.countryId, programId));
            }

            if (contact.countryId == USCountryId) {
              status.push(contact.countyId == null || contact.countyId === '');
              status.push(contact.stateId == null || contact.stateId === '');
            }

            if (contact.countryId != USCountryId) {
              status.push(contact.countryId == null);
            }
          }
          if (config.displayDepartment) {
            status.push(contact.departmentTypeId == null);
          }
          if (config.displayEmail) {
            status.push(contact.email == null || contact.email === '');
          }
          if (config.displayPhone) {
            status.push(contact.phone == null || contact.phone === '');
          }

          if (config.displayTitle) {
            status.push(contact.title == null || contact.title === '');
          }
        }

        contactHasMissingFields = status.some((n: boolean) => n);
      }
      return !contactHasMissingFields;
    },
    [isZipInValid]
  );

  const getContactTypes = useCallback(async (): Promise<ContactType[]> => {
    if (authResult.accessToken == null) return [];
    const fetchUrl = `${baseUrl}contacttypes`;

    const response = await customFetch<ContactType[]>(fetchUrl, {
      method: 'GET',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        Authorization: `Bearer ${authResult.accessToken}`,
      },
    });

    return response;
  }, [authResult, customFetch]);

  const getAllContactsByApplicationId = useCallback(
    async (applicationId: number): Promise<ContactProps[]> => {
      if (applicationId == null) return [];

      const fetchUrl = `${baseUrl}contacts/application/${applicationId}`;

      const response = await customFetch<ContactProps[]>(fetchUrl, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      return response;
    },
    [authResult, customFetch]
  );

  const getAllContactsByPartnerId = useCallback(
    async (partnerId: number | null): Promise<ContactProps[]> => {
      if (partnerId == null) return [];

      const fetchUrl = `${baseUrl}contacts?partnerId=${partnerId}`;

      const response = await customFetch<ContactProps[]>(fetchUrl, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      return response;
    },
    [authResult, customFetch]
  );

  return {
    getContact,
    saveContact,
    updateContactRelationship,
    postContactRelationship,
    deleteContactRelationship,
    getContactTypes,
    getAllContactsByApplicationId,
    getAllContactsByPartnerId,
    isContactValid,
    isZipInValid,
  };
}
